import {
  AlertIcon,
  Flex,
  FormProvider,
  Modal,
  SelectField,
  Stack,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useBreakpointValue,
  useForm,
} from '@agrotoken-tech/ui';
import { usePendingChangesAlert } from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import { CropPledgeUserRole, PledgeSectionStatus } from '@bcba/types';
import { ReactNode, useEffect, useState } from 'react';
import { CropPledgeAsideTab } from './CropPledgeAsideTab';

export interface CropPledgeAsideTabData {
  title: string;
  content: ReactNode;
  status: PledgeSectionStatus;
  isDisabled?: boolean;
}

export interface CropPledgeAsideProps {
  tabsData: CropPledgeAsideTabData[];
  cropPledgeUserRole: CropPledgeUserRole;
  alertsSpot?: ReactNode;
  theme?: any;
  hasUserPendingChanges?: boolean;
  setUserHasPendingChanges?: React.Dispatch<React.SetStateAction<boolean>>;
}
/**
 * CropPledgeAside Component
 *
 * This component renders a themed sidebar with tabs. Each tab contains a title, status, icon, and content.
 *
 * @param {Array} tabsData - An array of objects containing tab data. Each object should have the following properties:
 *    @param {string} tabsData.title - The title of the tab.
 *    @param {string} tabsData.status - The status of the tab.
 *    @param {JSX.Element} tabsData.icon - The icon of the tab.
 *    @param {JSX.Element} tabsData.content - The content of the tab.
 * @param {JSX.Element} alertsSpot - A JSX element to be rendered as an alert spot.
 * @param {Object} theme - The theme object to be applied to the ThemeProvider.
 * @param {boolean} isDirty - A boolean value to determine if the form is dirty.

 *
 * @returns {JSX.Element} - The rendered CropPledgeAside component.
 */
export const CropPledgeAside = ({
  tabsData = [],
  cropPledgeUserRole,
  alertsSpot = null,
  theme,
  hasUserPendingChanges = false,
  setUserHasPendingChanges = () => {},
}: CropPledgeAsideProps): JSX.Element => {
  const { t } = useTranslation();
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const {
    isPendingChangeAlertOpen,
    confirmPendingChangeAlertModal,
    cancelPendingChangeAlertModal,
    onPendingChangeAlertOpen,
  } = usePendingChangesAlert(hasUserPendingChanges, setUserHasPendingChanges);

  const formMethods = useForm({
    defaultValues: { asideTabs: String(tabIndex) },
  });
  const { watch } = formMethods;

  const shouldShowHorizontalOrientation = useBreakpointValue({
    base: true,
    sm: true,
    md: true,
    lg: false,
    xl: false,
  });

  const handleTabsChange = (index: number) => {
    setCurrentTabIndex(index);
    if (hasUserPendingChanges) {
      onPendingChangeAlertOpen();
    } else {
      setTabIndex(index);
    }
  };

  const handleConfirmPendingChangeAlertModal = () => {
    confirmPendingChangeAlertModal();
    setTabIndex(currentTabIndex);
  };

  const orientation = shouldShowHorizontalOrientation
    ? 'horizontal'
    : 'vertical';

  useEffect(() => {
    handleTabsChange(Number(watch('asideTabs')));
  }, [watch('asideTabs')]);

  return (
    <>
      <Stack gap={'8'} mt={4}>
        {alertsSpot}
        <Tabs
          isLazy
          orientation={orientation}
          aria-orientation={orientation}
          index={tabIndex}
          onChange={handleTabsChange}
        >
          {orientation === 'horizontal' ? (
            <Flex mb={8}>
              <FormProvider {...formMethods}>
                <SelectField
                  name="asideTabs"
                  label=""
                  options={tabsData.map(({ title }, index) => ({
                    value: String(index),
                    label: title,
                  }))}
                  value={String(tabIndex)}
                />
              </FormProvider>
            </Flex>
          ) : (
            <TabList textAlign={'start'} minW={'fit-content'} border={'none'}>
              {tabsData.map(({ title, status, isDisabled }) => (
                <CropPledgeAsideTab
                  key={title}
                  status={status}
                  isDisabled={isDisabled}
                  cropPledgeUserRole={cropPledgeUserRole}
                >
                  {title}
                </CropPledgeAsideTab>
              ))}
            </TabList>
          )}
          <TabPanels ps={{ base: 0, md: 4 }}>
            {tabsData.map(({ content, title }) => (
              <TabPanel key={title} p={{ base: 0, md: 4 }}>
                {content}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Stack>
      <Modal
        isOpen={isPendingChangeAlertOpen}
        onClose={cancelPendingChangeAlertModal}
        title={t('explorer.signUp.modal.exitForm.title')}
        text={t('explorer.signUp.modal.exitForm.text')}
        onClickConfirm={handleConfirmPendingChangeAlertModal}
        labelBtnConfirm={t('common.confirm')}
        variantBtnConfirm="primary"
        labelBtnClose={t('common.cancel')}
        icon={AlertIcon}
        iconVariant="warning"
      />
    </>
  );
};
